<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.admin_coach_passes.limit_title") }}</template>
        <template v-slot:body>
            <div class="is-flex is-flex-wrap-wrap is-align-items-center is-gap-2 mb-3">
                <input
                    class="input limit-input"
                    :class="{ 'is-danger': limitInvalid }"
                    type="text"
                    inputmode="numeric"
                    maxlength="2"
                    v-model="passesText"
                    :aria-label="translate('page.admin_coach_passes.limit_passes')"
                />
                <span>{{ translate("page.admin_coach_passes.limit_passes_per") }}</span>
                <input
                    class="input limit-input"
                    :class="{ 'is-danger': limitInvalid }"
                    type="text"
                    inputmode="numeric"
                    maxlength="3"
                    v-model="blockText"
                    :aria-label="translate('page.admin_coach_passes.limit_competitors')"
                />
                <span>{{ translate("page.admin_coach_passes.limit_competitors") }}</span>
                <a v-if="passesText !== '' || blockText !== ''" href="#" @click.prevent="resetLimit">
                    {{ translate("page.admin_coach_passes.limit_remove") }}
                </a>
            </div>
            <p v-if="limitInvalid" class="help is-danger mb-3">
                {{ translate("page.admin_coach_passes.limit_invalid") }}
            </p>

            <div>
                <Checkbox2 v-model="fillWithBlank" :disabled="!limitFilled || blankNames">
                    {{ translate("page.admin_coach_passes.limit_fill_with_blank") }}
                </Checkbox2>
            </div>
            <div>
                <Checkbox2 v-model="blankNames">
                    {{ translate("page.admin_coach_passes.limit_blank_names") }}
                </Checkbox2>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <button class="button is-primary" @click="save">
                    {{ translate("common.save") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { CoachPassSettings } from "../Api/coachPasses";
    import { parseLimit } from "./coachPassSettingsStorage";

    const emit = defineEmits<{ save: [settings: CoachPassSettings] }>();

    const { modal, open: openModal, close } = useModal();

    const passesText = ref("");
    const blockText = ref("");
    const fillWithBlank = ref(false);
    const blankNames = ref(false);
    const limitInvalid = ref(false);

    const limitFilled = computed(() => passesText.value.trim() !== "" && blockText.value.trim() !== "");

    function open(current: CoachPassSettings) {
        passesText.value = current.passesPerBlock?.toString() ?? "";
        blockText.value = current.competitorsPerBlock?.toString() ?? "";
        fillWithBlank.value = current.fillWithBlank;
        blankNames.value = current.blankNames;
        limitInvalid.value = false;

        openModal();
    }

    function resetLimit() {
        passesText.value = "";
        blockText.value = "";
        limitInvalid.value = false;
    }

    function save() {
        const limit = parseLimit(passesText.value, blockText.value);
        limitInvalid.value = !limit.valid;
        if (!limit.valid) {
            return;
        }

        emit("save", {
            passesPerBlock: limit.passesPerBlock,
            competitorsPerBlock: limit.competitorsPerBlock,
            fillWithBlank: fillWithBlank.value && limit.passesPerBlock !== null && !blankNames.value,
            blankNames: blankNames.value,
        });
        close();
    }

    defineExpose({ open });
</script>

<style scoped>
    .limit-input {
        width: 6rem;
    }
</style>
